import React, { useState, useEffect } from 'react';
import { Download, Filter, TrendingUp, TrendingDown, ChevronUp, ChevronDown } from 'lucide-react';
import { Button, Input } from '../ui';
import SearchableSelect from '../ui/SearchableSelect';
import api from '../../services/api';

const ProfitLossReportPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<any[]>([]);
  const [sortedRecords, setSortedRecords] = useState<any[]>([]);
  const [sortConfig, setSortConfig] = useState<{key: string, direction: 'asc' | 'desc'} | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    agencyId: '',
    itineraryId: '',
    travelDateFrom: '',
    travelDateTo: ''
  });
  const [agencies, setAgencies] = useState<any[]>([]);

  useEffect(() => {
    loadAgencies();
    loadRecords();
  }, []);

  const loadAgencies = async () => {
    try {
      const response = await api.get('/reports/vendors');
      if (response.data.success) {
        setAgencies(response.data.data);
      }
    } catch (error) {
      console.error('Error loading agencies:', error);
    }
  };

  const loadRecords = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Only use invoice dates if no travel dates are provided
      if (filters.travelDateFrom || filters.travelDateTo) {
        if (filters.travelDateFrom) params.append('travel_date_from', filters.travelDateFrom);
        if (filters.travelDateTo) params.append('travel_date_to', filters.travelDateTo);
      } else {
        if (filters.startDate) params.append('start_date', filters.startDate);
        if (filters.endDate) params.append('end_date', filters.endDate);
      }
      
      if (filters.agencyId) params.append('agency_id', filters.agencyId);
      if (filters.itineraryId) params.append('itinerary_id', filters.itineraryId);

      const apiUrl = `/reports/profit-loss?${params.toString()}`;
      console.log('🔍 Profit-Loss API Call:', apiUrl);
      console.log('📊 Filters:', filters);
      
      const response = await api.get(apiUrl);
      if (response.data.success) {
        setRecords(response.data.data);
        setSortedRecords(response.data.data);
        setAnalytics(response.data.analytics);
        console.log('📈 Analytics received:', response.data.analytics);
      }
    } catch (error) {
      console.error('Error loading profit loss records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    if (currency === 'INR') {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0
      }).format(amount);
    }
    if (currency === 'IDR') {
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0
      }).format(amount);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatIDR = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const calculateAnalytics = () => {
    if (analytics) {
      return {
        totalRevenue: analytics.total_received,
        totalExpenses: analytics.total_expenses,
        totalProfit: analytics.actual_profit,
        avgMargin: analytics.collection_rate,
        totalInvoiceAmount: analytics.total_invoice_amount,
        idealProfit: analytics.total_ideal_profit,
        idealProfitRate: analytics.ideal_profit_rate
      };
    }
    
    const totalRevenue = sortedRecords.reduce((sum, record) => sum + record.received_idr, 0);
    const totalExpenses = sortedRecords.reduce((sum, record) => sum + record.total_expenses, 0);
    const totalProfit = totalRevenue - totalExpenses;
    const avgMargin = sortedRecords.length > 0 ? sortedRecords.reduce((sum, record) => sum + record.margin_percent, 0) / sortedRecords.length : 0;
    
    return { totalRevenue, totalExpenses, totalProfit, avgMargin, totalInvoiceAmount: 0, idealProfit: 0, idealProfitRate: 0 };
  };

  const calculatedAnalytics = calculateAnalytics();

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...records].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setSortedRecords(sorted);
  };

  const getSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <ChevronUp className="w-4 h-4 opacity-30" />;
    }
    return sortConfig.direction === 'asc' ? 
      <ChevronUp className="w-4 h-4" /> : 
      <ChevronDown className="w-4 h-4" />;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">📊 Profit & Loss Report</h1>
        <p className="text-gray-600">Analyze profitability across converted itineraries</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Invoice Amount</p>
              <p className="text-2xl font-bold text-blue-600">{formatIDR(calculatedAnalytics.totalInvoiceAmount || 0)}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Revenue (Received)</p>
              <p className="text-2xl font-bold text-green-600">{formatIDR(calculatedAnalytics.totalRevenue)}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Expenses</p>
              <p className="text-2xl font-bold text-red-600">{formatIDR(calculatedAnalytics.totalExpenses)}</p>
            </div>
            <TrendingDown className="w-8 h-8 text-red-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Ideal Profit</p>
              <p className={`text-2xl font-bold ${(calculatedAnalytics.idealProfit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatIDR(calculatedAnalytics.idealProfit || 0)}
              </p>
            </div>
            {(calculatedAnalytics.idealProfit || 0) >= 0 ? 
              <TrendingUp className="w-8 h-8 text-green-500" /> : 
              <TrendingDown className="w-8 h-8 text-red-500" />
            }
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Ideal Profit %</p>
              <p className={`text-2xl font-bold ${(calculatedAnalytics.idealProfitRate || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(calculatedAnalytics.idealProfitRate || 0).toFixed(1)}%
              </p>
            </div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${(calculatedAnalytics.idealProfitRate || 0) >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              <span className={`text-sm font-bold ${(calculatedAnalytics.idealProfitRate || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3 mb-6">
        <Button variant="secondary" className="flex items-center gap-2">
          <Download className="w-4 h-4" />
          Excel
        </Button>
        <Button variant="secondary" className="flex items-center gap-2">
          <Download className="w-4 h-4" />
          PDF
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date From</label>
            <Input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date To</label>
            <Input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Agency Name</label>
            <SearchableSelect
              options={agencies.map(agency => {
                const agencyName = typeof agency === 'string' ? agency : agency.name || agency.agency_name || agency;
                return { value: agencyName === 'All Agencies' ? '' : agencyName, label: agencyName };
              })}
              value={filters.agencyId}
              onChange={(value) => handleFilterChange('agencyId', value as string)}
              placeholder="Select Agency"
              className="w-full"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date From</label>
            <Input
              type="date"
              value={filters.travelDateFrom}
              onChange={(e) => handleFilterChange('travelDateFrom', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date To</label>
            <Input
              type="date"
              value={filters.travelDateTo}
              onChange={(e) => handleFilterChange('travelDateTo', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Itinerary ID</label>
            <Input
              type="text"
              value={filters.itineraryId}
              onChange={(e) => handleFilterChange('itineraryId', e.target.value)}
              placeholder="WH_1234"
              className="w-full"
            />
          </div>
        </div>
        
        <Button onClick={loadRecords} disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Profit & Loss Records ({sortedRecords.length})</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100" onClick={() => handleSort('itinerary_id')}>
                  <div className="flex items-center gap-1">
                    Itinerary
                    {getSortIcon('itinerary_id')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100" onClick={() => handleSort('guest_name')}>
                  <div className="flex items-center gap-1">
                    Guest
                    {getSortIcon('guest_name')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100" onClick={() => handleSort('agency_name')}>
                  <div className="flex items-center gap-1">
                    Agency
                    {getSortIcon('agency_name')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[90px] cursor-pointer hover:bg-gray-100" onClick={() => handleSort('travel_date')}>
                  <div className="flex items-center gap-1">
                    Travel Date
                    {getSortIcon('travel_date')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] cursor-pointer hover:bg-gray-100" onClick={() => handleSort('invoice_amount')}>
                  <div className="flex items-center gap-1">
                    Invoice
                    {getSortIcon('invoice_amount')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[90px] cursor-pointer hover:bg-gray-100" onClick={() => handleSort('received_amount')}>
                  <div className="flex items-center gap-1">
                    Received
                    {getSortIcon('received_amount')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[110px] cursor-pointer hover:bg-gray-100" onClick={() => handleSort('received_idr')}>
                  <div className="flex items-center gap-1">
                    Received (IDR)
                    {getSortIcon('received_idr')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[90px] cursor-pointer hover:bg-gray-100" onClick={() => handleSort('total_expenses')}>
                  <div className="flex items-center gap-1">
                    Expenses
                    {getSortIcon('total_expenses')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100" onClick={() => handleSort('profit_loss')}>
                  <div className="flex items-center gap-1">
                    Profit/Loss
                    {getSortIcon('profit_loss')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] cursor-pointer hover:bg-gray-100" onClick={() => handleSort('margin_percent')}>
                  <div className="flex items-center gap-1">
                    Margin %
                    {getSortIcon('margin_percent')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedRecords.map((record, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-3 py-4 text-sm font-medium text-gray-900">
                    <div className="truncate max-w-[100px]" title={record.itinerary_id}>
                      {record.itinerary_id}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-900">
                    <div className="truncate max-w-[120px]" title={record.guest_name}>
                      {record.guest_name}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-900">
                    <div className="truncate max-w-[100px]" title={record.agency_name}>
                      {record.agency_name}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-900">
                    {record.travel_date ? new Date(record.travel_date).toLocaleDateString('en-GB') : 'N/A'}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-900 text-right">
                    {formatCurrency(record.invoice_amount, record.currency)}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-900 text-right">
                    {formatCurrency(record.received_amount, record.currency)}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-900 text-right">
                    {formatIDR(record.received_idr)}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-900 text-right">
                    {formatIDR(record.total_expenses)}
                  </td>
                  <td className="px-3 py-4 text-sm">
                    <div className={`flex items-center gap-1 justify-end ${
                      record.profit_loss >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {record.profit_loss >= 0 ? 
                        <TrendingUp className="w-4 h-4" /> : 
                        <TrendingDown className="w-4 h-4" />
                      }
                      <span>{formatIDR(record.profit_loss)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-center">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      record.margin_percent >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {record.margin_percent}%
                    </span>
                  </td>
                  <td className="px-3 py-4 text-sm">
                    <Button variant="secondary" size="sm" className="text-xs px-2 py-1">
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {sortedRecords.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-gray-500">No profit & loss records found for the selected criteria</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfitLossReportPage;